import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, LayoutDashboard, LogOut } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import API from '../../lib/api';
import { getImageUrl } from '../../lib/utils-img';

export default function Header() {
  const { user, isAdmin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [locDropdown, setLocDropdown] = useState(false);
  const [locations, setLocations] = useState([]);
  const [siteLogo, setSiteLogo] = useState('');
  const [logoHeight, setLogoHeight] = useState(48);
  const [navItems, setNavItems] = useState([
    { label: 'Home', path: '/', dropdown: false, visible: true },
    { label: 'Locations', path: '/locations', dropdown: true, visible: true },
    { label: 'About', path: '/about', dropdown: false, visible: true },
    { label: 'Contact', path: '/contact', dropdown: false, visible: true },
    { label: 'Policies', path: '/policies', dropdown: false, visible: true },
  ]);
  const dropRef = useRef(null);
  const isHome = location.pathname === '/';

  useEffect(() => {
    API.get('/nav').then(r => {
      setLocations(r.data.locations);
      const nav = r.data.navigation || {};
      if (nav.header_items && nav.header_items.length > 0) {
        setNavItems(nav.header_items.filter(i => i.visible !== false));
      }
    }).catch(() => {});
    API.get('/site-settings').then(r => {
      const logo = r.data?.site?.logo_url;
      if (logo) setSiteLogo(logo);
      const h = r.data?.site?.logo_height;
      if (h) setLogoHeight(h);
    }).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);
  useEffect(() => { setMobileOpen(false); setLocDropdown(false); }, [location.pathname]);
  useEffect(() => {
    const fn = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setLocDropdown(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const isActive = (path) => location.pathname === path || (path === '/locations' && location.pathname.startsWith('/locations'));

  const headerBg = (scrolled || !isHome)
    ? 'bg-[#121212]/90 backdrop-blur-xl border-b border-white/[0.08]'
    : '';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerBg}`} data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <Link to="/" className="shrink-0 z-10" data-testid="header-logo">
            {siteLogo ? (
              <img src={getImageUrl(siteLogo)} alt="PlushResidence" style={{ height: `${logoHeight}px` }} className="w-auto object-contain" data-testid="header-logo-img" />
            ) : (
              <span className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-white">
                Plush<span className="text-gold">.</span>
              </span>
            )}
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5 px-2 py-1.5 rounded-full border bg-white/[0.04] backdrop-blur-md border-white/[0.10]">
            {navItems.map((item, idx) => (
              <div key={`${item.path}-${idx}`} className="relative" ref={item.dropdown ? dropRef : undefined}>
                {item.dropdown ? (
                  <button
                    onClick={() => setLocDropdown(!locDropdown)}
                    className={`flex items-center gap-1 px-5 py-2 text-[13px] font-medium rounded-full transition-all duration-300
                      ${isActive(item.path) ? 'bg-gold text-white shadow-sm' : 'text-white/50 hover:text-white hover:bg-white/[0.08]'}`}
                    data-testid="locations-dropdown-trigger"
                  >
                    {item.label}
                    <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${locDropdown ? 'rotate-180' : ''}`} />
                  </button>
                ) : (
                  <Link to={item.path}
                    className={`px-5 py-2 text-[13px] font-medium rounded-full transition-all duration-300 block
                      ${isActive(item.path) ? 'bg-gold text-white shadow-sm' : 'text-white/50 hover:text-white hover:bg-white/[0.08]'}`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </Link>
                )}
                <AnimatePresence>
                  {item.dropdown && locDropdown && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.18 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-52 bg-[#202020] rounded-xl shadow-2xl border border-white/[0.10] py-2 overflow-hidden" data-testid="locations-dropdown">
                      <Link to="/locations" className="block px-5 py-2.5 text-[13px] text-white hover:bg-white/[0.05] hover:text-gold transition-colors font-semibold border-b border-white/[0.08] mb-1">All Locations</Link>
                      {locations.map(loc => (
                        <Link key={loc.slug} to={`/locations/${loc.slug}`} className="block px-5 py-2.5 text-[13px] text-white/50 hover:bg-white/[0.05] hover:text-gold transition-colors" data-testid={`dropdown-${loc.slug}`}>{loc.name}</Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3 z-10">
            {isAdmin && (
              <Link to="/admin" className="text-[12px] font-medium uppercase tracking-wider text-white/55 hover:text-white transition-colors" data-testid="admin-link">
                <LayoutDashboard className="w-4 h-4 inline mr-1" />Admin
              </Link>
            )}
            {user ? (
              <button onClick={() => { logout(); navigate('/'); }} className="px-5 py-2.5 rounded-full text-[13px] font-medium border border-white/[0.1] text-white/60 hover:bg-white/[0.05] transition-all duration-300" data-testid="logout-btn">
                <LogOut className="w-3.5 h-3.5 inline mr-1.5" />Logout
              </button>
            ) : (
              <>
                <Link to="/login" className="px-5 py-2.5 rounded-full text-[13px] font-medium border border-white/[0.10] text-white/60 hover:text-white hover:bg-white/[0.05] transition-all duration-300" data-testid="login-link">
                  Login
                </Link>
                <Link to="/locations" className="px-6 py-2.5 rounded-full text-[13px] font-semibold bg-gold text-white hover:bg-gold-dark shadow-md shadow-gold/20 transition-all duration-300" data-testid="header-cta">
                  Explore Stays
                </Link>
              </>
            )}
          </div>

          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 rounded-xl z-10 text-white hover:bg-white/[0.05] transition-colors" data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="lg:hidden bg-[#1c1c1c] border-t border-white/[0.08] shadow-2xl overflow-hidden">
            <div className="px-6 py-6 space-y-1">
              {navItems.map((item, idx) => (
                <div key={`${item.path}-${idx}`}>
                  {item.dropdown ? (
                    <>
                      <Link to="/locations" className="block px-4 py-3 text-sm font-semibold text-white hover:text-gold rounded-xl hover:bg-white/[0.03]">{item.label}</Link>
                      <div className="ml-4 space-y-0.5">{locations.map(loc => (
                        <Link key={loc.slug} to={`/locations/${loc.slug}`} className="block px-4 py-2.5 text-sm text-white/55 hover:text-gold rounded-lg hover:bg-white/[0.03]">{loc.name}</Link>
                      ))}</div>
                    </>
                  ) : (
                    <Link to={item.path} className={`block px-4 py-3 text-sm font-semibold rounded-xl hover:bg-white/[0.03] ${isActive(item.path) ? 'text-gold' : 'text-white hover:text-gold'}`}>{item.label}</Link>
                  )}
                </div>
              ))}
              <div className="pt-4 border-t border-white/[0.08]">
                {isAdmin && <Link to="/admin" className="block px-4 py-3 text-sm font-semibold text-gold">Admin Dashboard</Link>}
                {user ? <button onClick={() => { logout(); navigate('/'); }} className="block w-full text-left px-4 py-3 text-sm text-white/55">Logout</button>
                  : <Link to="/login" className="block px-4 py-3 text-sm text-white/50">Login</Link>}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
